'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SensorsIcon from '@mui/icons-material/Sensors';
import { Box, Button, Typography } from '@mui/material';

import { LiveBadge } from '@/components/common/LiveBadge';
import { useT } from '@/hooks';
import { LiveEvent } from '@/types';
import { formatVolume } from '@/utils';

import { ROUTES } from '@/constants';

interface LiveEventCardHeaderProps {
  event: LiveEvent;
}

export function LiveEventCardHeader({ event }: LiveEventCardHeaderProps) {
  const router = useRouter();
  const { t } = useT();

  function handleGameViewClick(e: React.MouseEvent) {
    e.stopPropagation();
    router.push(ROUTES.GAME_DETAIL(event.sportSlug || 'live', event.id));
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        {/* Live Indicator */}
        <LiveBadge dotSize={8} fontSize="0.8125rem" />

        {/* Game Meta Info */}
        <Typography
          noWrap
          sx={{
            fontSize: '0.875rem',
            fontWeight: 700,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {event.status}
          <Box
            component="span"
            sx={{
              width: 4,
              height: 4,
              bgcolor: 'text.secondary',
              borderRadius: '50%',
              opacity: 0.5,
            }}
          />
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {formatVolume(event?.volume)}
          </Box>
          <Box
            component="span"
            sx={{
              width: 4,
              height: 4,
              bgcolor: 'text.secondary',
              borderRadius: '50%',
              opacity: 0.5,
            }}
          />
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {event?.league || ''}
          </Box>
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <SensorsIcon sx={{ fontSize: '1.125rem' }} />
        </Box>

        <Button
          size="small"
          onClick={handleGameViewClick}
          sx={{
            height: 32,
            fontSize: '0.8125rem',
            textTransform: 'none',
            color: 'text.primary',
            fontWeight: 700,
            px: 1.5,
            borderRadius: '8px',
            bgcolor: 'action.hover',
            border: '1px solid transparent',
            '&:hover': {
              bgcolor: 'action.selected',
              borderColor: 'divider',
            },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {event?.gameViewCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
              }}
            >
              {event?.gameViewCount}
            </Box>
          )}
          {t('sports.gameView', { defaultValue: 'Game View' })}
          <ChevronRightIcon sx={{ fontSize: '1.125rem', opacity: 0.7 }} />
        </Button>
      </Box>
    </Box>
  );
}
